import type { SessionState } from './api.ts'
import type { Campaign } from './campaign.tsx'

/** The screen's names for roles (docs/decisions.md 2026-09-30). One place, so they never drift. */
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'SuperAdmin',
  org_admin: 'Org Admin',
  consultant: 'Consultant',
  campaign_manager: 'Campaign Manager',
  field_lieutenant: 'Field lieutenant',
  phone_lieutenant: 'Phone bank lead',
  campaign_worker: 'Worker',
  canvasser: 'Canvasser',
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role
}

/**
 * Roles a person may invite onto a campaign from its Team page, by the role
 * they hold there. The readable copy of auth.may_grant_role (migration 0084) —
 * the database refuses anything outside it, so this only decides what the
 * picker offers. Canvassers are created on the Canvassers page, not invited.
 */
const INVITABLE_BY: Record<string, string[]> = {
  super_admin: ['consultant', 'campaign_manager', 'field_lieutenant', 'phone_lieutenant', 'campaign_worker'],
  org_admin: ['consultant', 'campaign_manager', 'field_lieutenant', 'phone_lieutenant', 'campaign_worker'],
  consultant: ['consultant', 'campaign_manager', 'field_lieutenant', 'phone_lieutenant', 'campaign_worker'],
  campaign_manager: ['field_lieutenant', 'phone_lieutenant', 'campaign_worker'],
}

export function invitableRoles(roleHere: string): string[] {
  return INVITABLE_BY[roleHere] ?? []
}

type Authenticated = Extract<SessionState, { state: 'authenticated' }>

export function isPlatformAdmin(session: SessionState | undefined): boolean {
  if (session?.state !== 'authenticated') return false
  return (session as Authenticated).grants.some(
    (g) => g.scope_type === 'platform' && (g.role === 'super_admin' || g.role === 'org_admin'),
  )
}

export function isSuperAdmin(session: SessionState | undefined): boolean {
  if (session?.state !== 'authenticated') return false
  return (session as Authenticated).grants.some(
    (g) => g.scope_type === 'platform' && g.role === 'super_admin',
  )
}

export interface NavItem {
  to: string
  label: string
}

export interface NavGroup {
  heading: string | null
  items: NavItem[]
}

/**
 * The campaign workspace menu, by job (owner, 2026-10-25, row W1): the data
 * side (Lists) and the walk side (Canvasses — the old NextGen system) each get
 * their own section, so a consultant cutting mail lists never needs Canvasses
 * and a field manager never needs to know what a universe is. An item shows
 * only when the campaign bought the product AND the role here may use it — the
 * same two questions every route asks, answered once so the menu never offers
 * a page that will refuse.
 */
export function workspaceNav(campaign: Campaign | undefined): NavGroup[] {
  if (!campaign) return [{ heading: null, items: [{ to: '/overview', label: 'Overview' }] }]

  const full = campaign.can_build_universes
  const worker = campaign.role === 'campaign_worker'
  const groups: NavGroup[] = [{ heading: null, items: [{ to: '/overview', label: 'Overview' }] }]

  // Lists: who to target, and the lists cut from it.
  const lists: NavItem[] = []
  if (full) lists.push({ to: '/universes', label: 'Universes' })
  if (full || campaign.can_cut_walk_lists) lists.push({ to: '/lists', label: 'Lists' })
  // Drawing turf decides who gets knocked on — a targeting decision, so the
  // map is for the full roles.
  if (full && campaign.walk_purchased) lists.push({ to: '/turf', label: 'Map areas' })
  if (lists.length > 0) groups.push({ heading: 'Lists', items: lists })

  // Canvasses: the walk side.
  const walk: NavItem[] = []
  if ((campaign.walk_purchased || campaign.walk_app) && (full || campaign.can_manage_canvassers)) {
    if (campaign.can_manage_canvassers) {
      walk.push({ to: '/canvasses', label: 'Canvasses' })
      // Walkers are walk-APP accounts; paper alone does not need them (0088).
      if (campaign.walk_app) walk.push({ to: '/canvassers', label: 'Walkers' })
      walk.push({ to: '/scripts', label: 'Scripts' })
    }
    if (full) walk.push({ to: '/surveys', label: 'Surveys' })
  }
  if (full || worker) walk.push({ to: '/keying', label: 'Paper results' })
  if (walk.length > 0) groups.push({ heading: 'Canvasses', items: walk })

  groups.push({ heading: 'Results', items: [{ to: '/progress', label: 'Progress' }] })
  if (campaign.can_view_team) groups.push({ heading: null, items: [{ to: '/team', label: 'Team' }] })
  return groups
}

/** The platform menu, SuperAdmin and Org Admin only. */
/**
 * The platform menu (2026-10-01): Today first, then the business, then the
 * platform's own plumbing. Billing is the SuperAdmin's alone — Org Admins
 * never receive prices — so it is offered only to them.
 */
export function platformNav(superAdmin: boolean): NavGroup[] {
  return [
    { heading: null, items: [{ to: '/platform/today', label: 'Today' }] },
    {
      heading: 'Business',
      items: [
        { to: '/platform/customers', label: 'Customers' },
        { to: '/platform/campaigns', label: 'Campaigns' },
        { to: '/platform/one-off', label: 'Get started' },
        ...(superAdmin ? [{ to: '/platform/billing', label: 'Billing' }] : []),
      ],
    },
    {
      heading: 'Platform',
      items: [
        { to: '/platform/people', label: 'People' },
        { to: '/platform/voter-data', label: 'Voter data' },
      ],
    },
  ]
}
